import './bulkEdit.scss';

import { DataTypes, UOMS } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Button, ColumnLayout, FormField, Header, Input, Modal, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';

import { constants } from '../../../common/Localization';
import { BOOL_OPTIONS } from '../constants';
import { generateUomOptions, UOMOption } from '../utils';
import { PortfolioTableActionGroupProp } from './PortfolioTableActionGroup';
type PickedTypes = 'resourceName' | 'schemaProperties' | 'schemaPropertyMap' | 'onConfirmBulkEdit';
type Props = Pick<PortfolioTableActionGroupProp, PickedTypes> & {
    countText: string;
    selectAll: boolean;
    setVisible: (visible: boolean) => void;
    visible: boolean;
};

const BulkEditComponent = (props: Props): JSX.Element => {
    const headerOptions: SelectProps.Option[] = props.schemaProperties.map(schemaProp => {
        return {
            disabled: schemaProp.isId,
            label: schemaProp.text,
            value: schemaProp.value,
        };
    });

    const [ selectedHeaderOption, setSelectedHeaderOption ] = useState<SelectProps.Option | null>(null);
    const [ selectedDataType, setSelectedDataType ] = useState<DataTypes | undefined>(undefined);
    const [ selectedCustomUpdateOptions, setSelectedCustomUpdateOptions ] = useState<SelectProps.Options>([]);
    const [ updatedValue, setUpdatedValue ] = useState<string | number | boolean>('');
    const [ updateForbiddenMsg, setUpdateForbiddenMsg ] = useState('');
    const [ uomOptions, setUomOptions ] = useState<UOMOption[]>([]);
    const [ selectedUOM, setSelectedUOM ] = useState<SelectProps.Option>({});
    const [ selectedOption, setSelectedOption ] = useState<SelectProps.Option>({});

    useEffect(() => {
        const selectedHeader = selectedHeaderOption?.value;
        if (!selectedHeader) {
            return;
        }

        setSelectedDataType(props.schemaPropertyMap[selectedHeader].dataType);
        const currentCustomUpdateOptions: SelectProps.Option[] = props.schemaPropertyMap[selectedHeader].customUpdateOptions?.map(selectedOption => {
            return {
                value: selectedOption.id.toString(),
                label: selectedOption.label
            };
        }) || [];
        setSelectedCustomUpdateOptions(currentCustomUpdateOptions);
        if (props.schemaPropertyMap[selectedHeader].dataType === DataTypes.BOOLEAN) {
            setSelectedOption(BOOL_OPTIONS[0]);
            setUpdatedValue(BOOL_OPTIONS[0].value === 'true' || '');
        } else if (props.schemaPropertyMap[selectedHeader].customUpdateOptions?.length) {
            setSelectedOption(currentCustomUpdateOptions[0]);
            setUpdatedValue(currentCustomUpdateOptions[0].value || '');
        }

        if (props.schemaPropertyMap[selectedHeader]?.messages?.updateForbidden) {
            setUpdateForbiddenMsg(props.schemaPropertyMap[selectedHeader].messages.updateForbidden);
        } else {
            setUpdateForbiddenMsg('');
        }

        if (props.schemaPropertyMap[selectedHeader]?.UOM) {
            const newUOMOptions = generateUomOptions(props.schemaPropertyMap[selectedHeader].UOM);
            setUomOptions(newUOMOptions);
            setSelectedUOM(newUOMOptions[0]);
        } else {
            setUomOptions([]);
            setSelectedUOM({});
        }
    }, [selectedHeaderOption, props.schemaPropertyMap]);

    const hideComponent = (): void => props.setVisible(false);

    const bulkEditHeader = `${constants.EDIT_BUTTON_TEXT} ${props.countText}`;

    const disableUpdateBtn = (): boolean => {
        return !props.countText || !selectedHeaderOption?.value || updateForbiddenMsg.length !== 0;
    };

    const onConfirm = (): void => {
        props.onConfirmBulkEdit(props.selectAll, selectedHeaderOption?.value || '', updatedValue, selectedUOM?.label as UOMS);
        hideComponent();
    };

    const bulkEditEditSection = (): JSX.Element => {
        if (selectedDataType === DataTypes.BOOLEAN) {
            return (
                <Select
                    placeholder={constants.BULK_UPDATE_INPUT_PLACEHOLDER}
                    options={BOOL_OPTIONS}
                    selectedOption={selectedOption}
                    filteringType="auto"
                    onChange={({ detail }) => {
                        setSelectedOption(detail.selectedOption);
                        setUpdatedValue(detail.selectedOption.value ? detail.selectedOption.value === 'true' : '');
                    }}
                    data-testid="selectYesNo"
                />
            );
        } else if (selectedCustomUpdateOptions.length) {
            return (
                <Select
                    placeholder={constants.BULK_UPDATE_INPUT_PLACEHOLDER}
                    options={selectedCustomUpdateOptions}
                    selectedOption={selectedOption}
                    onChange={({ detail }) => {
                        setSelectedOption(detail.selectedOption);
                        setUpdatedValue(detail.selectedOption.value?.toString() ?? '');
                    }}
                    filteringType="auto"
                    data-testid="selectValue"
                />
            );
        } else {
            return (
                <Input
                    value={updatedValue.toString()}
                    onChange={({ detail }) => setUpdatedValue(detail.value)}
                    placeholder={constants.BULK_UPDATE_INPUT_PLACEHOLDER}
                    data-testid="inputValue"
                />
            );
        }
    };
    return (
        <Modal
            onDismiss={() => props.setVisible(false)}
            visible={props.visible}
            header={
                <Header
                    variant="h2"
                >
                    {bulkEditHeader}
                </Header>
            }
            footer={
                <Box float="right" margin="xs">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={hideComponent}>{constants.CANCEL_BUTTON_TEXT}</Button>
                        <Button variant="primary" onClick={onConfirm} disabled={disableUpdateBtn()} data-testid='confirmBtn'>
                            {constants.UPDATE_BUTTON_TEXT}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <FormField>
                <ColumnLayout columns={2}>
                    <Select
                        selectedOption={selectedHeaderOption}
                        onChange={({ detail }) => {
                            setSelectedOption({});
                            setSelectedHeaderOption(detail.selectedOption);
                        }}
                        options={headerOptions}
                        filteringType="auto"
                        placeholder={constants.BULK_UPDATE_FIELD_SELECTION_PLACEHOLDER}
                        data-testid="selectHeader"
                    />
                    {updateForbiddenMsg ?
                        <strong>{updateForbiddenMsg}</strong> :
                        bulkEditEditSection()
                    }
                </ColumnLayout>
                {
                    (uomOptions.length > 0) && (
                        <ColumnLayout columns={2}>
                            <Select
                                selectedOption={selectedUOM}
                                onChange={({ detail }) => setSelectedUOM(detail.selectedOption)}
                                options={uomOptions}
                            />
                        </ColumnLayout>
                    )}
            </FormField>
        </Modal>
    );
};

export default BulkEditComponent;
